import React, { useState, useEffect } from 'react';
import { 
  Phone, Building2, Calendar, Settings, Users, Database, Save, RotateCcw, 
  CheckCircle, AlertCircle, Plus, Search, ChevronDown, ChevronUp, User, Mail,
  Edit, Eye
} from 'lucide-react';
import mobileService from '../services/mobileService'; 
const SalesMobilePhones = ({ selectedConnectionId, onSaveSuccess, onCancel }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  

  const [formData, setFormData] = useState({
    connection_id: selectedConnectionId,
    current_provider: 'Vodafone',
    new_provider: 'Vodafone',
    existing_costs: 0,
    existing_costs_currency: 'GBP',
    existing_line_rentals_service_charges: 0,
    existing_line_rentals_currency: 'GBP',
    additional_service_charge: 0,
    existing_sims_to_migrate: 0,
    new_sim_cards_to_provide: 0,
    todays_date: new Date().toISOString().split('T')[0],
    contract_end_date: new Date().toISOString().split('T')[0],
    take_mobiles_over_immediately: true,
    staff_levels: [
      { staff_level: 'director_premium', count: 0, cost_per_unit: 0 },
      { staff_level: 'director', count: 0, cost_per_unit: 0 },
      { staff_level: 'manager', count: 0, cost_per_unit: 0 },
      { staff_level: 'standard', count: 0, cost_per_unit: 0 }
    ],
    data_plans: [
      { data_allowance: 'unlimited', data: 'Unlimited', count: 0, cost_per_unit: 0 },
      { data_allowance: '40 GB', data: '40GB', count: 0, cost_per_unit: 0 },
      { data_allowance: '20 GB', data: '20GB', count: 0, cost_per_unit: 0 },
      { data_allowance: '10 GB', data: '10GB', count: 0, cost_per_unit: 0 }
    ]
  });

 
  // Load mobile data when selectedConnectionId changes
  useEffect(() => {
  const loadMobileData = async () => {
    if (selectedConnectionId) {
      setLoading(true);
      try {
        const mobileData = await mobileService.getMobileService(selectedConnectionId);
        if (mobileData) {
          const defaultDataPlans = [
            { data_allowance: 'unlimited', data: 'Unlimited', count: 0, cost_per_unit: 0 },
            { data_allowance: '40 GB', data: '40GB', count: 0, cost_per_unit: 0 },
            { data_allowance: '20 GB', data: '20GB', count: 0, cost_per_unit: 0 },
            { data_allowance: '10 GB', data: '10GB', count: 0, cost_per_unit: 0 }
          ];

          const defaultStaffLevels = [
            { staff_level: 'director_premium', count: 0, cost_per_unit: 0 },
            { staff_level: 'director', count: 0, cost_per_unit: 0 },
            { staff_level: 'manager', count: 0, cost_per_unit: 0 },
            { staff_level: 'standard', count: 0, cost_per_unit: 0 }
          ];

          const mergedDataPlans = defaultDataPlans.map(defaultPlan => {
            const existingPlan = mobileData.data_plans?.find(p =>
              p.data_allowance?.toLowerCase() === defaultPlan.data_allowance.toLowerCase()
            );
            return existingPlan 
              ? { ...existingPlan, count: Number(existingPlan.count) || 0 }  // ← parse to number
              : defaultPlan;
          });

          // ✅ Same merge pattern as data_plans
          const mergedStaffLevels = defaultStaffLevels.map(defaultLevel => {
            const existingLevel = mobileData.staff_levels?.find(s =>
              s.staff_level === defaultLevel.staff_level
            );
            return existingLevel 
              ? { ...existingLevel, count: Number(existingLevel.count) || 0 }  // ← parse to number
              : defaultLevel;
          });

          setFormData(prev => ({
            ...prev,
            ...mobileData,
            staff_levels: mergedStaffLevels,   // ✅ use merged instead of raw
            data_plans: mergedDataPlans,
            connection_id: selectedConnectionId
          }));
        }
      } catch (error) {
        console.error('Error loading mobile data:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  loadMobileData();
}, [selectedConnectionId]);

  const handleSaveMobile = async () => {
    setLoading(true);
    
    try {
      let response;
      //console.log("Form data",formData);
      if (formData.id) {
        response = await mobileService.updateMobileService(formData.id, formData);
      } else {
        response = await mobileService.createMobileService(formData);
      }
      
    
      const savedData = response.data;
      
      setMessage('Mobile contract saved successfully!');
      
      // Call the onSaveSuccess callback with the saved data
      if (onSaveSuccess) {
        onSaveSuccess(savedData); // Pass the entire response
      }
      
      // Close the modal if needed
      if (onCancel) {
        setTimeout(() => {
          onCancel();
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to save connection:', error);
      setMessage('Error: ' + (error.message || 'Failed to save mobile contract'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Enhanced number input handler with click-to-clear and blur-to-zero behavior
  const handleNumberInputChange = (e, updateFunction) => {
    const { value } = e.target;
    const numValue = value === '' ? 0 : parseFloat(value) || 0;
    updateFunction(numValue);
  };

  const handleNumberInputFocus = (e) => {
    // Clear the input if it's 0
    if (e.target.value === '0') {
      e.target.value = '';
    }
  };

  const handleNumberInputBlur = (e, updateFunction) => {
    // If empty, set to 0
    if (e.target.value === '') {
      e.target.value = '0';
      updateFunction(0);
    }
  };

  const handleStaffLevelChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      staff_levels: prev.staff_levels.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleDataPlanChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      data_plans: prev.data_plans.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const resetForm = () => {
    setFormData({
      connection_id: selectedConnectionId,
      current_provider: 'Vodafone',
      new_provider: 'Vodafone',
      existing_costs: 0,
      existing_costs_currency: 'GBP',
      existing_line_rentals_service_charges: 0,
      existing_line_rentals_currency: 'GBP',
      additional_service_charge: 0,
      existing_sims_to_migrate: 0,
      new_sim_cards_to_provide: 0,
      todays_date: selectedDate,
      contract_end_date: selectedDate,
      take_mobiles_over_immediately: true,
      staff_levels: [
        { staff_level: 'director_premium', count: 0, cost_per_unit: 0 },
        { staff_level: 'director', count: 0, cost_per_unit: 0 },
        { staff_level: 'manager', count: 0, cost_per_unit: 0 },
        { staff_level: 'standard', count: 0, cost_per_unit: 0 }
      ],
      data_plans: [
        { data_allowance: 'unlimited', data: 'Unlimited', count: 0, cost_per_unit: 0 },
        { data_allowance: '40 GB', data: '40GB', count: 0, cost_per_unit: 0 },
        { data_allowance: '20 GB', data: '20GB', count: 0, cost_per_unit: 0 },
        { data_allowance: '10 GB', data: '10GB', count: 0, cost_per_unit: 0 }
      ]
    });
  };

  const staffLevelLabels = {
    director_premium: 'Director Premium',
    director: 'Director',
    manager: 'Manager',
    standard: 'Standard'
  };
 // Sync Unlimited data plan count with total SIMs
useEffect(() => {
  const totalSims = 
    (Number(formData.existing_sims_to_migrate) || 0) + 
    (Number(formData.new_sim_cards_to_provide) || 0);

  setFormData(prev => ({
    ...prev,
    data_plans: (prev.data_plans || []).map((plan, i) =>
      i === 0 ? { ...plan, count: totalSims } : plan
    )
  }));
}, [formData.existing_sims_to_migrate, formData.new_sim_cards_to_provide]);

  return (
    
     
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="space-y-8">
          {/* Provider Information */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform hover:shadow-2xl transition-all duration-300">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4">
              <div className="flex items-center">
                <Building2 className="w-6 h-6 text-white mr-3" />
                <h3 className="text-xl font-semibold text-white">Provider Information</h3>
              </div>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    Current Provider
                  </label>
                  <select
                    name="current_provider"
                    value={formData.current_provider}
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm"
                  >
                    <option value="Vodafone">📱 Vodafone</option>
                    <option value="EE">📱 EE</option>
                    <option value="O2">📱 O2</option>
                    <option value="Three">📱 Three</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700 flex items-center">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                    New Provider
                  </label>
                  <select
                    name="new_provider"
                    value={formData.new_provider}
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring focus:ring-blue-200 transition-all duration-200 bg-white shadow-sm"
                  >
                    <option value="Vodafone">📱 Vodafone</option>
                    <option value="EE">📱 EE</option>
                    <option value="O2">📱 O2</option>
                    <option value="Three">📱 Three</option>
                  </select>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4">
                <label className="text-sm font-semibold text-gray-700 mb-3 block">
                  Existing Line Rentals & Service Charges
                </label>

                <div className="mb-4">
                  <label className="text-sm text-gray-600 font-medium block mb-1">Line Rental</label>
                  <div className="flex items-center bg-white rounded-lg p-3 border-2 border-gray-200 focus-within:border-blue-500 focus-within:ring focus-within:ring-blue-200 transition-all duration-200">
                    <span className="text-lg font-bold text-green-600 mr-2">£</span>
                    <input
                      type="number"
                      name="existing_costs"
                      value={formData.existing_costs}
                      onChange={handleInputChange}
                      onFocus={handleNumberInputFocus}
                      onBlur={(e) => handleNumberInputBlur(e, (value) => 
                        setFormData(prev => ({ ...prev, existing_costs: value }))
                      )}
                      step="0.01"
                      min="0"
                      className="flex-1 bg-transparent border-none outline-none text-lg font-medium"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-gray-600 font-medium block mb-1">Service Charge</label>
                  <div className="flex items-center bg-white rounded-lg p-3 border-2 border-gray-200 focus-within:border-blue-500 focus-within:ring focus-within:ring-blue-200 transition-all duration-200">
                    <span className="text-lg font-bold text-green-600 mr-2">£</span>
                    <input
                      type="number"
                      name="existing_line_rentals_service_charges"
                      value={formData.existing_line_rentals_service_charges}
                      onChange={handleInputChange}
                      onFocus={handleNumberInputFocus}
                      onBlur={(e) => handleNumberInputBlur(e, (value) => 
                        setFormData(prev => ({ ...prev, existing_line_rentals_service_charges: value }))
                      )}
                      step="0.01"
                      min="0"
                      className="flex-1 bg-transparent border-none outline-none text-lg font-medium"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* SIM Connections */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform hover:shadow-2xl transition-all duration-300">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4">
              <div className="flex items-center">
                <Phone className="w-6 h-6 text-white mr-3" />
                <h3 className="text-xl font-semibold text-white">SIM Connections</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                  <label className="text-sm font-semibold text-gray-700 mb-3 block">Existing SIMs to migrate</label>
                  <div className="flex items-center justify-center">
                    <input
                      type="number"
                      name="existing_sims_to_migrate"
                      value={formData.existing_sims_to_migrate}
                      onChange={handleInputChange}
                      onFocus={handleNumberInputFocus}
                      onBlur={(e) => handleNumberInputBlur(e, (value) => 
                        setFormData(prev => ({ ...prev, existing_sims_to_migrate: value }))
                      )}
                      min="0"
                      className="w-20 h-12 text-center text-xl font-bold border-2 border-blue-200 rounded-lg focus:border-blue-500 focus:ring focus:ring-blue-200 transition-all duration-200"
                    />
                  </div>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100">
                  <label className="text-sm font-semibold text-gray-700 mb-3 block">New SIM cards to provide</label>
                  <div className="flex items-center justify-center">
                    <input
                      type="number"
                      name="new_sim_cards_to_provide"
                      value={formData.new_sim_cards_to_provide}
                      onChange={handleInputChange}
                      onFocus={handleNumberInputFocus}
                      onBlur={(e) => handleNumberInputBlur(e, (value) => 
                        setFormData(prev => ({ ...prev, new_sim_cards_to_provide: value }))
                      )}
                      min="0"
                      className="w-20 h-12 text-center text-xl font-bold border-2 border-green-200 rounded-lg focus:border-green-500 focus:ring focus:ring-green-200 transition-all duration-200"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contract Dates */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform hover:shadow-2xl transition-all duration-300">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4">
              <div className="flex items-center">
                <Calendar className="w-6 h-6 text-white mr-3" />
                <h3 className="text-xl font-semibold text-white">Contract Dates</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Today's Date</label>
                  <input
                    type="date"
                    name="todays_date"
                    value={formData.todays_date}
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring focus:ring-purple-200 transition-all duration-200"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-gray-700">Contract End Date</label>
                  <input
                    type="date"
                    name="contract_end_date"
                    value={formData.contract_end_date}
                    onChange={handleInputChange}
                    className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring focus:ring-purple-200 transition-all duration-200"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform hover:shadow-2xl transition-all duration-300">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4">
              <div className="flex items-center">
                <Settings className="w-6 h-6 text-white mr-3" />
                <h3 className="text-xl font-semibold text-white">Options</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-xl p-4 border border-orange-100">
                <label className="text-sm font-semibold text-gray-700 mb-3 block">Take Mobiles over immediately</label>
                <select
                  name="take_mobiles_over_immediately"
                  value={formData.take_mobiles_over_immediately ? 'Yes' : 'No'}
                  onChange={(e) => handleInputChange({
                    target: {
                      name: 'take_mobiles_over_immediately',
                      type: 'checkbox',
                      checked: e.target.value === 'Yes'
                    }
                  })}
                  className="w-full p-3 border-2 border-orange-200 rounded-xl focus:border-orange-500 focus:ring focus:ring-orange-200 transition-all duration-200 bg-white"
                >
                  <option value="Yes">✅ Yes</option>
                  <option value="No">❌ No</option>
                </select>
              </div>
            </div>
          </div>

          {/* Staff Levels */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform hover:shadow-2xl transition-all duration-300">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4">
              <div className="flex items-center">
                <Users className="w-6 h-6 text-white mr-3" />
                <h3 className="text-xl font-semibold text-white">Mobile Equipment Requirements</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid gap-4">
                {formData.staff_levels.map((staffLevel, index) => (
                  <div key={index} className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 border border-indigo-100 hover:shadow-md transition-all duration-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-indigo-500 rounded-full mr-3"></div>
                        <span className="font-semibold text-gray-800">{staffLevelLabels[staffLevel.staff_level]}</span>
                        <span className="ml-4 px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">Level</span>
                      </div>
                      <input
                        type="number"
                        value={staffLevel.count}
                        onChange={(e) => handleNumberInputChange(e, (value) => handleStaffLevelChange(index, 'count', value))}
                        onFocus={handleNumberInputFocus}
                        onBlur={(e) => handleNumberInputBlur(e, (value) => handleStaffLevelChange(index, 'count', value))}
                        min="0"
                        className="w-16 h-10 text-center font-bold border-2 border-indigo-200 rounded-lg focus:border-indigo-500 focus:ring focus:ring-indigo-200 transition-all duration-200"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Data Plans */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden transform hover:shadow-2xl transition-all duration-300">
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4">
              <div className="flex items-center">
                <Database className="w-6 h-6 text-white mr-3" />
                <h3 className="text-xl font-semibold text-white">Mobile Tariff Selector</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="grid gap-4">
                {(formData.data_plans || []).slice(0, 1).map((dataPlan, index) => (
                  <div key={index} className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-3 h-3 bg-cyan-500 rounded-full mr-3"></div>
                        <span className="font-semibold text-gray-800">{dataPlan.data || dataPlan.data_allowance}</span>
                        <span className="ml-4 px-3 py-1 bg-cyan-100 text-cyan-700 text-xs font-medium rounded-full">Data</span>
                      </div>
                      <input
                        type="number"
                        value={formData.data_plans?.[0]?.count ?? 0}
                        onChange={(e) => handleNumberInputChange(e, (value) => handleDataPlanChange(index, 'count', value))}
                        onFocus={handleNumberInputFocus}
                        onBlur={(e) => handleNumberInputBlur(e, (value) => handleDataPlanChange(index, 'count', value))}
                        min="0"
                        className="w-16 h-10 text-center font-bold border-2 border-cyan-200 rounded-lg focus:border-cyan-500 focus:ring focus:ring-cyan-200 transition-all duration-200"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-6">
            <button
              onClick={handleSaveMobile}
              disabled={loading}
              className="group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
            >
              <div className="flex items-center justify-center">
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-300" />
                )}
                {loading ? 'Saving Contract...' : 'Save Mobile Contract'}
              </div>
            </button>
            <button
              onClick={resetForm}
              className="group px-8 py-4 bg-gradient-to-r from-gray-600 to-gray-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
            >
              <div className="flex items-center justify-center">
                <RotateCcw className="w-5 h-5 mr-2 group-hover:rotate-180 transition-transform duration-500" />
                Reset Form
              </div>
            </button>
          </div>

          {/* Message Display */}
          {message && (
            <div className={`p-4 rounded-xl border-l-4 transform transition-all duration-500 ${
              message.includes('Error') 
                ? 'bg-red-50 border-red-500 text-red-700' 
                : 'bg-green-50 border-green-500 text-green-700'
            }`}>
              <div className="flex items-center">
                {message.includes('Error') ? (
                  <AlertCircle className="w-5 h-5 mr-2" />
                ) : (
                  <CheckCircle className="w-5 h-5 mr-2" />
                )}
                <span className="font-medium">{message}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesMobilePhones;